/**
 * The live status socket's wire contract, as the kiosk sees it — mirrored from the api's own source.
 *
 * Source: `iss-kiosk-api` @ `develop` 99113e2 (PR 220, [03-us-kad-6j-a]) and 46d20b92 ([03-us-kad-6j-e]),
 * byte for byte:
 *
 * | Here | There |
 * | --- | --- |
 * | {@link LIVE_SOCKET_PATH} | `src/modules/live-status/live-status.gateway.ts` — `LIVE_STATUS_PATH` |
 * | {@link CONFIG_PUBLISHED} | `src/shared/live/live-frames.ts` — `SERVER_FRAME_TYPE.configPublished` |
 * | {@link SESSION_STARTED} | `src/shared/live/live-frames.ts` — `KIOSK_CLIENT_FRAME_TYPE.sessionStarted` |
 * | {@link SESSION_ENDED} | `src/shared/live/live-frames.ts` — `KIOSK_CLIENT_FRAME_TYPE.sessionEnded` |
 * | {@link KIOSK_FRAME_REFUSED} | `src/modules/live-status/live-connection.ts:27,32` — `LIVE_CLOSE.kioskSentAFrame.code` and `LIVE_CLOSE.kioskFrameInvalid.code` |
 * | {@link MAX_FIRST_NAME_LENGTH} | `src/modules/live-status/live-connection.ts:112` — `MAX_FIRST_NAME_LENGTH` (⚠️ *proposed* there) |
 * | {@link ONE_LETTER} | `src/modules/live-status/live-connection.ts:113` — `ONE_LETTER` |
 * | {@link REFUSED_IN_A_NAME} | `src/modules/live-status/live-connection.ts:120` — `REFUSED_IN_A_NAME` |
 *
 * ⭐ **Every value this app copies from the api's live-status gateway is in this file** (the enrolment
 * contract mirrors its own, in `enrolment.contract.ts`), so a change there has one place to land here. The three name bounds are what `readSessionStarted` holds a `session.started` candidate to [KAD-6j-e
 * AC-3]; `src/lib/sessionCandidate.ts` applies them, so a candidate the gateway would refuse is never sent.
 *
 * ⚠️ **Hand-written, as `enrolment.contract.ts` is, and for the same reason**: types are meant to be generated
 * (convention § 6), and a gateway has no OpenAPI schema to generate them from. A change on either side is a
 * change to BOTH files.
 */

/** The gateway's one endpoint (`GET /socket`, Upgrade). The cookie is the whole identity [0206]. */
export const LIVE_SOCKET_PATH = '/socket';

/** The one frame type the server sends a kiosk: `{type: 'config.published', data: {venueId}}` [0354 § 2]. */
export const CONFIG_PUBLISHED = 'config.published';

/**
 * The two frame types a kiosk may send [0319 § Ruling 3, amending 0354 § 2; KAD-6j-e AC-4]:
 * `{type: 'session.started', data: {candidate}}` and `{type: 'session.ended', data: {}}`.
 */
export const SESSION_STARTED = 'session.started';
export const SESSION_ENDED = 'session.ended';

/**
 * The close code the gateway uses when it refuses a kiosk's frame: a type off the kiosk's list
 * (`kiosk_sends_no_frames`) or, since KAD-6j-e, a session frame whose payload is not exactly right
 * (`kiosk_frame_invalid`). Either means this client sent something it must not.
 */
export const KIOSK_FRAME_REFUSED = 1008;

/**
 * The longest `firstName` the gateway accepts, in UTF-16 units after trimming. ⚠️ **Proposed** on the api's
 * side (*"64 is the kiosk-name bound (proposed)"*): if it moves there, it moves here.
 */
export const MAX_FIRST_NAME_LENGTH = 64;

/** What `lastInitial` must be: exactly one letter. */
export const ONE_LETTER = /^\p{L}$/u;

/**
 * What a `firstName` may not carry: control, format, line and paragraph separator, lone surrogate and
 * private-use characters.
 */
export const REFUSED_IN_A_NAME = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Cs}\p{Co}]/u;
